import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    Input,
    Text,
    Badge,
    Flex,
    Button,
    createListCollection,
    HStack,
    VStack,
    Icon,
    IconButton,
} from '@chakra-ui/react';
import {
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DialogTitle,
    DialogBackdrop,
    DialogCloseTrigger,
} from './ui/dialog';
import { toaster } from "./ui/toaster.jsx";
import { Field } from './ui/field.jsx';
import { getStopLossMonitors, updateStopLossMonitorPrice } from '../api';
import { HiOutlineRefresh, HiOutlineDotsVertical, HiOutlineBell, HiOutlineXCircle, HiOutlineExclamationCircle } from 'react-icons/hi';

const StopLossMonitors = ({ hiddenTitle = false }) => {
    const [monitors, setMonitors] = useState([]);
    const [filteredMonitors, setFilteredMonitors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingMonitor, setEditingMonitor] = useState(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [newTargetPrice, setNewTargetPrice] = useState('');
    const [statusFilter, setStatusFilter] = useState('MONITORING');

    const extractValueFromSelection = (selectionValue) => {
        if (!selectionValue) return '';
        if (typeof selectionValue === 'string') return selectionValue;
        if (selectionValue.value !== undefined) return selectionValue.value;
        if (Array.isArray(selectionValue) && selectionValue.length > 0) {
            return selectionValue[0]?.value || '';
        }
        return '';
    };

    const fetchMonitors = async () => {
        setIsLoading(true);
        try {
            const data = await getStopLossMonitors();
            setMonitors(data);
            const status = extractValueFromSelection(statusFilter);
            setFilteredMonitors(applyFilters(data, status));
        } catch (error) {
            toaster.create({
                title: '获取数据失败',
                description: error.message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchMonitors();
    }, []);

    useEffect(() => {
        const status = extractValueFromSelection(statusFilter);
        setFilteredMonitors(applyFilters(monitors, status));
    }, [statusFilter, monitors]);

    const applyFilters = (data, statusObj) => {
        return data.filter(monitor => !statusObj || monitor.status === statusObj);
    };

    const getStatusBadge = (status) => {
        const config = {
            'MONITORING': { color: 'green', label: '活跃监控', icon: HiOutlineBell },
            'TRIGGERED': { color: 'red', label: '已触发', icon: HiOutlineExclamationCircle },
            'CANCELLED': { color: 'gray', label: '已取消', icon: HiOutlineXCircle },
        };
        const item = config[status] || { color: 'gray', label: status, icon: HiOutlineDotsVertical };
        return (
            <Badge variant="subtle" colorPalette={item.color} borderRadius="full" px={3} py={0.5}>
                <HStack gap={1}>
                    <Icon as={item.icon} size="xs" />
                    <Text fontSize="xs">{item.label}</Text>
                </HStack>
            </Badge>
        );
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('zh-CN', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined) return '-';
        return Number(price).toFixed(2);
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        try {
            await fetchMonitors();
            toaster.create({ title: "监控数据已更新", type: 'success' });
        } catch (error) {
            toaster.create({ title: "刷新失败", description: error.message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditTargetPrice = (monitor) => {
        setEditingMonitor(monitor);
        setNewTargetPrice(monitor.target_price);
        setIsEditDialogOpen(true);
    };

    const saveTargetPrice = async () => {
        try {
            await updateStopLossMonitorPrice(editingMonitor.id, newTargetPrice);
            toaster.create({
                title: '修改成功',
                description: `${editingMonitor.symbol} 目标价已设为 ${newTargetPrice}`,
                type: 'success',
            });
            await fetchMonitors();
            setIsEditDialogOpen(false);
        } catch (error) {
            toaster.create({
                title: '修改失败',
                description: error.response?.data?.detail || error.message,
                type: 'error',
            });
        }
    };

    return (
        <Box w="full">
            {!hiddenTitle && (
                <Flex justify="space-between" align="center" mb={6}>
                    <HStack gap={2}>
                        <Icon as={HiOutlineBell} color="brand.500" />
                        <Text fontSize="xl" fontWeight="800">止损价格监控</Text>
                    </HStack>
                    <Button
                        variant="ghost"
                        onClick={handleRefresh}
                        isLoading={isLoading}
                        size="sm"
                        borderRadius="lg"
                    >
                        <HiOutlineRefresh /> 刷新
                    </Button>
                </Flex>
            )}

            <Box border="1px solid" borderColor="gray.100" _dark={{ borderColor: "white/5" }} borderRadius="xl" overflow="hidden">
                <Table.Root size="md" variant="line">
                    <Table.Header bg="gray.50/50" _dark={{ bg: "white/5" }}>
                        <Table.Row>
                            <Table.ColumnHeader fontWeight="bold">标的</Table.ColumnHeader>
                            <Table.ColumnHeader isNumeric fontWeight="bold">实时价格</Table.ColumnHeader>
                            <Table.ColumnHeader isNumeric fontWeight="bold">触发价格</Table.ColumnHeader>
                            <Table.ColumnHeader fontWeight="bold">状态</Table.ColumnHeader>
                            <Table.ColumnHeader fontWeight="bold">创建时间</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" fontWeight="bold">操作</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {filteredMonitors.length > 0 ? (
                            filteredMonitors.map(monitor => (
                                <Table.Row
                                    key={monitor.id}
                                    _hover={{ bg: "gray.50/50", _dark: { bg: "white/2" } }}
                                >
                                    <Table.Cell>
                                        <Text fontWeight="800" color="brand.600">{monitor.symbol}</Text>
                                    </Table.Cell>
                                    <Table.Cell isNumeric fontWeight="600">{formatPrice(monitor.stock_price)}</Table.Cell>
                                    <Table.Cell isNumeric color="red.600" fontWeight="700">
                                        {formatPrice(monitor.target_price)}
                                    </Table.Cell>
                                    <Table.Cell>{getStatusBadge(monitor.status)}</Table.Cell>
                                    <Table.Cell fontSize="xs" color="gray.500">{formatDateTime(monitor.created_at)}</Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <Button
                                            size="xs"
                                            variant="subtle"
                                            colorPalette="brand"
                                            onClick={() => handleEditTargetPrice(monitor)}
                                            isDisabled={monitor.status !== 'MONITORING'}
                                            borderRadius="md"
                                        >
                                            修改
                                        </Button>
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        ) : (
                            <Table.Row>
                                <Table.Cell colSpan={6} textAlign="center" py={12}>
                                    <VStack gap={2}>
                                        <Icon as={HiOutlineBell} size="32" color="gray.200" />
                                        <Text color="gray.400" fontSize="sm">
                                            {isLoading ? '加载中...' : '当前无活跃止损监控'}
                                        </Text>
                                    </VStack>
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table.Root>
            </Box>

            <DialogRoot
                open={isEditDialogOpen}
                onOpenChange={(e) => setIsEditDialogOpen(e.open)}
                size="sm"
            >
                <DialogBackdrop />
                <DialogContent borderRadius="2xl" boxShadow="2xl">
                    <DialogHeader borderBottom="1px solid" borderColor="gray.100" _dark={{ borderColor: "white/10" }} pb={4}>
                        <DialogTitle fontSize="xl">修改止损触发价</DialogTitle>
                        <Text fontSize="sm" color="gray.500">{editingMonitor?.symbol}</Text>
                        <DialogCloseTrigger />
                    </DialogHeader>
                    <DialogBody py={6}>
                        <VStack gap={6} align="stretch">
                            <SimpleGrid columns={2} gap={4}>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" mb={1}>当前实时价</Text>
                                    <Text fontWeight="800" fontSize="lg">{formatPrice(editingMonitor?.stock_price)}</Text>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" mb={1}>原定触发价</Text>
                                    <Text fontWeight="800" fontSize="lg" color="red.600">{formatPrice(editingMonitor?.target_price)}</Text>
                                </Box>
                            </SimpleGrid>

                            <Field label="新触发价格 (美元)">
                                <Input
                                    value={newTargetPrice}
                                    onChange={(e) => setNewTargetPrice(e.target.value)}
                                    type="number"
                                    step="0.01"
                                    size="lg"
                                    borderRadius="xl"
                                    autoFocus
                                />
                            </Field>
                        </VStack>
                    </DialogBody>
                    <DialogFooter bg="gray.50" _dark={{ bg: "white/5" }} p={4} borderRadius="0 0 1rem 1rem">
                        <Button variant="ghost" onClick={() => setIsEditDialogOpen(false)} borderRadius="xl">
                            取消
                        </Button>
                        <Button
                            colorPalette="brand"
                            px={8}
                            onClick={saveTargetPrice}
                            borderRadius="xl"
                        >
                            更新监控
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </DialogRoot>
        </Box>
    );
};

export default StopLossMonitors;
const SimpleGrid = ({ columns, gap, children }) => (
    <Box display="grid" gridTemplateColumns={`repeat(${columns}, 1fr)`} gap={gap}>
        {children}
    </Box>
);
